/**
 * WorkspaceDocumentPage
 *
 * Renders a single workspace document (indexed HTML) with a sidebar for
 * table-of-contents, document references, chat references, and version history.
 *
 * Key responsibilities:
 * - Fetches the HTML for the selected document revision via `useGetDocumentHtml`
 *   (keyed by workspaceId, documentId, blobId, and revisionVersion).
 * - Parses the raw HTML into sanitised body (`rawHTML`) and extracted CSS (`rawCSS`).
 * - Provides zoom, print, and (future) search controls via `WorkspaceDocumentHeader`.
 * - Displays a read-only TOC sidebar populated by the API per document revision.
 */
import useWorkspace from "@/hooks/useWorkspace";
import useUsers from "@/hooks/useUsers";
import useDocumentLink from "@/hooks/useDocumentLink";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resolveWorkspaceViewerReturnUrl } from "@/lib/navigation/documentViewerReturn";
import { useDocumentViewerBreadcrumbs } from "@/hooks/useDocumentViewerBreadcrumbs";
import { useDocumentViewerStackSync } from "@/hooks/useDocumentViewerContextSync";
import Workspace, { WorkspaceDocument } from "@/models/workspace/Workspace";
import AppRoutes from "@/constants/routes/AppRoutes";
import DocumentSidebar from "@/components/content-sidebar/document-sidebar/document-sidebar";
import { DocumentTab } from "@/components/content-sidebar/document-sidebar/document-tab-triggers";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";
import PageContent from "../PageContent";
import DocumentRenderer from "@/partials/document-renderer/DocumentRenderer";
import { prepareStoredDocumentHtml } from "@/partials/document-renderer/utils/storedDocumentHtml";
import DocumentViewerToolbar from "@/partials/document-renderer/components/DocumentViewerToolbar";
import ScrollToTopFab from "@/partials/document-renderer/components/ScrollToTopFab";
import { handlePrintDocumentOnly } from "@/partials/document-renderer/utils/helpers";
import PrintStyles from "@/partials/document-renderer/components/PrintStyles";
import WorkspaceDocumentHeader from "./components/WorkspaceDocumentHeader";
import WorkspaceDocumentStyles from "./components/WorkspaceDocumentStyles";
import { useDocumentZoom } from "./hooks/useDocumentZoom";
import { useDocumentRenderTiming } from "./hooks/useDocumentRenderTiming";
import { isWorkspaceDocumentHistoric } from "./utils/workspaceItemUtils";
import {
  documentMatchesRoute,
  resolveDocumentHtmlTarget,
} from "./utils/documentHtmlTarget";
import { parseDocumentViewerHighlightParams } from "@/partials/document-renderer/utils/referenceHighlightQuery";
import { useDocumentSearch } from "@/partials/document-renderer/hooks/useDocumentSearch";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { DataSource } from "@/config/datasources";
import { logger } from "@/lib/logger";

const WorkspaceDocumentPage: React.FC = () => {
  const { workspaceId, blobName } = useParams<{
    workspaceId: string;
    blobName: string;
  }>();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const { useGetDocumentHtml, useQuerySingle } = useWorkspace();
  const { useQueryUsersFavoriteDocuments, useQuerySubscriptions } = useUsers();
  const { useQueryByDocumentId: useQueryDocumentLinks } = useDocumentLink();
  const favoriteDocumentsQuery = useQueryUsersFavoriteDocuments();
  const subscriptionsQuery = useQuerySubscriptions();
  const {
    setWorkspaceDocument,
    workspaceDocument,
    setWorkspace: setWorkspaceInStore,
    selectedVersion,
    setSelectedVersion,
  } = useWorkspaceDocumentVersionStore();

  // The fetched HTML, keyed by the blob it belongs to. `html` derives to null
  // the moment the viewer targets a different blob, so switching version or
  // document never shows the previous blob's content while the new one loads.
  const [fetchedHtml, setFetchedHtml] = useState<{
    blobId: string;
    value: string;
  } | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [isRendering, setIsRendering] = useState(false);
  const hasShownHtmlErrorRef = useRef(false);

  // Null while the persisted store still describes the previously opened
  // document, so nothing is requested for it (LAR-487).
  const htmlTarget = resolveDocumentHtmlTarget(
    workspaceId,
    blobName,
    workspaceDocument,
    selectedVersion
  );
  const routeDocument = htmlTarget ? workspaceDocument : null;
  const blobIdToFetch = htmlTarget?.blobId || blobName || "";
  const revisionVersionToFetch = htmlTarget?.revisionVersion;
  const fetchedDocumentHtml = useGetDocumentHtml(
    workspaceId,
    htmlTarget?.documentId,
    htmlTarget?.blobId,
    revisionVersionToFetch
  );
  const fetchedWorkspace = useQuerySingle(workspaceId);

  useEffect(() => {
    // Only reset selectedVersion when navigating to a different document
    if (
      selectedVersion &&
      blobName &&
      selectedVersion.blobName !== blobName &&
      workspaceDocument?.revisions &&
      !workspaceDocument.revisions.some((rev) => rev.blobId === blobName)
    ) {
      setSelectedVersion(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobName, setSelectedVersion, workspaceDocument]);

  useEffect(() => {
    if (fetchedWorkspace?.isError && fetchedWorkspace?.isFetched) {
      navigate(AppRoutes.workspaceNotFound, { replace: true });
    }
  }, [fetchedWorkspace?.isError, fetchedWorkspace?.isFetched, navigate]);

  useEffect(() => {
    try {
      if (fetchedWorkspace?.isFetched && fetchedWorkspace?.data) {
        setWorkspace(fetchedWorkspace.data);
        setWorkspaceInStore(fetchedWorkspace.data);

        const getMatchingRevision = (
          document: WorkspaceDocument,
          routeBlobOrId?: string
        ) =>
          document.revisions.find(
            (revision) => revision.blobId === routeBlobOrId
          );

        const findDocumentInFolders = (
          folders: Workspace["folders"]
        ): {
          document: WorkspaceDocument;
          matchingRevision?: WorkspaceDocument["revisions"][number];
        } | null => {
          for (const folder of folders) {
            const foundDoc = folder.documents.find((doc) =>
              documentMatchesRoute(doc, blobName)
            );
            if (foundDoc) {
              return {
                document: foundDoc,
                matchingRevision: getMatchingRevision(foundDoc, blobName),
              };
            }
            const nestedDocResult = findDocumentInFolders(folder.folders);
            if (nestedDocResult) return nestedDocResult;
          }
          return null;
        };

        const topLevelDocument = fetchedWorkspace.data.documents.find((doc) =>
          documentMatchesRoute(doc, blobName)
        );
        const nestedResult = findDocumentInFolders(fetchedWorkspace.data.folders);
        // The stored document only stands in when it is the one the URL names;
        // otherwise it is the previously opened document, not this one.
        const storedDocument =
          workspaceDocument && documentMatchesRoute(workspaceDocument, blobName)
            ? workspaceDocument
            : null;
        const document = topLevelDocument ?? nestedResult?.document ?? storedDocument;
        const matchingRevision =
          (topLevelDocument && getMatchingRevision(topLevelDocument, blobName)) ??
          nestedResult?.matchingRevision;

        if (!document) {
          navigate(AppRoutes.chatFileNotFound, { replace: true });
          return;
        }

        setWorkspaceDocument({
          ...document,
          workspaceId: workspaceId,
        });

        if (matchingRevision) {
          setSelectedVersion({
            blobName: matchingRevision.blobId,
            name: document.name,
            updatedAt: matchingRevision.updatedAt,
            revisionVersion: matchingRevision.revisionVersion,
            size: matchingRevision.size,
            documentType: document.documentType,
          });
        } else if (document.id === blobName) {
          setSelectedVersion({
            blobName: document.blobName,
            name: document.name,
            updatedAt: document.updatedAt ? new Date(document.updatedAt) : new Date(),
            revisionVersion: document.activeRevision,
            size: document.size,
            documentType: document.documentType,
          });
        }
      }
    } catch (error) {
      logger.error("Failed to resolve workspace document from the route", {
        operation: "resolveWorkspaceDocument",
        error,
        workspaceId,
        blobId: blobName,
      });
      navigate(AppRoutes.chatFileNotFound, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchedWorkspace?.data,
    fetchedWorkspace?.isFetched,
    blobName,
    navigate,
    setWorkspaceDocument,
    setSelectedVersion,
  ]);

  useEffect(() => {
    if (fetchedDocumentHtml.isSuccess && fetchedDocumentHtml.data && blobIdToFetch) {
      setFetchedHtml({ blobId: blobIdToFetch, value: fetchedDocumentHtml.data });
      setIsRendering(true);
      const timer = setTimeout(() => setIsRendering(false), 50);
      return () => clearTimeout(timer);
    }
  }, [fetchedDocumentHtml.isSuccess, fetchedDocumentHtml.data, blobIdToFetch]);

  const html =
    fetchedHtml && fetchedHtml.blobId === blobIdToFetch
      ? fetchedHtml.value
      : null;

  useEffect(() => {
    if (fetchedDocumentHtml.isError && !hasShownHtmlErrorRef.current) {
      hasShownHtmlErrorRef.current = true;
      showErrorToast(
        "Der opstod en fejl ved hentning af dokumentet. Prøv at genindlæse siden."
      );
    }
    if (!fetchedDocumentHtml.isError) {
      hasShownHtmlErrorRef.current = false;
    }
  }, [fetchedDocumentHtml.isError]);

  // One inert parse + one sanitize for the whole document (LAR-487). The
  // renderer is told the markup is already sanitized so it skips a second pass.
  const { rawHTML, rawCSS } = useMemo(
    () => prepareStoredDocumentHtml(html ?? ""),
    [html]
  );

  const favoriteSubscribe = useMemo(() => {
    if (!workspaceId || !workspaceDocument?.id) return undefined;

    const entityPath = `workspace/${workspaceId}/document/${workspaceDocument.id}`;
    const favoriteIds =
      favoriteDocumentsQuery.data?.favoriteWorkspaceDocuments.map(
        (fav) => fav.documentId
      ) ?? [];
    const isSubscribed =
      Array.isArray(subscriptionsQuery.data) &&
      subscriptionsQuery.data.some((s) => s.entityPath === entityPath);

    return {
      documentId: workspaceDocument.id,
      workspaceId,
      title: workspaceDocument.name,
      isFavorite: favoriteIds.includes(workspaceDocument.id),
      isSubscribed,
      isReady:
        favoriteDocumentsQuery.isFetched && subscriptionsQuery.isFetched,
    };
  }, [
    workspaceId,
    workspaceDocument?.id,
    workspaceDocument?.name,
    favoriteDocumentsQuery.data,
    favoriteDocumentsQuery.isFetched,
    subscriptionsQuery.data,
    subscriptionsQuery.isFetched,
  ]);

  // Zoom, container ref, and doc-root management. `blobName` is the document
  // identity (a revision switch only moves `blobIdToFetch`), so zoom resets on
  // navigation to another document but survives switching revision.
  const {
    zoomLevel,
    setZoomLevel,
    handleFitToWidth,
    containerRef,
    docRootReady,
  } = useDocumentZoom(rawHTML, blobName);

  useDocumentRenderTiming({
    workspaceId,
    documentId: workspaceDocument?.id,
    blobId: blobIdToFetch || undefined,
    htmlChars: html?.length ?? 0,
    htmlReceivedAt: fetchedDocumentHtml.dataUpdatedAt,
    isRendered: docRootReady && !!rawHTML,
  });

  const {
    highlightIds,
    scrollToId,
    refMatchText,
    refChunkText,
    docSearchTerm,
  } = useMemo(
    () => parseDocumentViewerHighlightParams(searchParams),
    [searchParams]
  );

  // Seeded with the global search term so the searched word is marked (LAR-264)
  const searchProps = useDocumentSearch({
    seedQuery: docSearchTerm,
    documentKey: blobIdToFetch || blobName,
  });

  const cameFromChat = window.location.pathname.includes("/chat/");

  const fallbackTitle = workspaceDocument?.name || "Dokumentvisning";

  const resolveBaseReturnUrl = useCallback(() => {
    if (!workspaceId) {
      return AppRoutes.workspaces;
    }
    return resolveWorkspaceViewerReturnUrl({
      workspaceId,
      workspace,
      documentId: workspaceDocument?.id,
      cameFromChat,
    });
  }, [workspaceId, workspace, workspaceDocument?.id, cameFromChat]);

  const { parentBreadcrumbs, flatParentBreadcrumbs, currentTitle, currentFullTitle, handleViewerBack } =
    useDocumentViewerBreadcrumbs({
      fallbackTitle,
      resolveBaseReturnUrl,
    });

  useDocumentViewerStackSync({
    title: fallbackTitle,
    rootLabel:
      !cameFromChat && workspace?.name
        ? `Workspace: ${workspace.name}`
        : undefined,
    workspaceId: cameFromChat ? undefined : workspaceId,
  });

  // Derive the documentId (blobId for the current revision) used by the sidebar
  const sidebarDocumentId =
    routeDocument?.revisions.find(
      (revision) => revision.revisionVersion === revisionVersionToFetch
    )?.blobId ?? routeDocument?.blobName;

  // Start the renderer's document-links query (same key) alongside the HTML
  // download instead of after the first render. For a document with links the
  // first parse then already has them, rather than parsing the whole document
  // a second time when they arrive (LAR-487).
  useQueryDocumentLinks(sidebarDocumentId ?? "");

  // "Kunne ikke vises" must only render once the HTML query actually settled
  // empty or failed. Anything earlier in the load cascade (workspace fetched
  // but effects not yet run, query key settling between two fetches, the frame
  // before the heavy sanitize render commits) is still loading — rendering the
  // error there froze it on screen for the whole sanitize pass on large
  // documents. Refetches while html is already on screen are deliberately NOT
  // loading: unmounting the rendered document into a spinner mid-view is what
  // caused the reload scroll dance (LAR-252).
  // A cached success-with-null (e.g. a visit before indexing finished) must
  // not count as settled while a refetch is in flight — show loading until the
  // fresh answer lands.
  const htmlSettledEmpty =
    fetchedDocumentHtml.isSuccess &&
    !fetchedDocumentHtml.data &&
    !fetchedDocumentHtml.isFetching;
  const isPageLoading =
    fetchedWorkspace.isLoading ||
    (!routeDocument && !fetchedWorkspace.isError) ||
    (!html && !htmlSettledEmpty && !fetchedDocumentHtml.isError);

  return (
    <PageContent
      title={currentTitle}
      titleFull={currentFullTitle}
      parent={parentBreadcrumbs}
      parentOverflowMenu={flatParentBreadcrumbs}
      onNavigateBack={handleViewerBack}
      rightSidebar={
        cameFromChat ? (
          <DocumentSidebar
            contextualDefault={DocumentTab.CHAT_REFERENCES}
            availableTabs={[
              DocumentTab.TABLE_OF_CONTENTS,
              DocumentTab.DOCUMENT_METADATA,
              DocumentTab.CHAT_REFERENCES,
              DocumentTab.DOCUMENT_REFERENCES,
              DocumentTab.VERSION_HISTORY,
            ]}
            documentId={sidebarDocumentId}
            selectionResetKey={blobName}
            blobId={blobIdToFetch || undefined}
            currentRevisionVersion={revisionVersionToFetch}
            isLoading={isPageLoading}
            workspaceDocument={routeDocument ?? undefined}
            createReferenceTarget={{
              documentId: sidebarDocumentId,
              source: DataSource.Workspace,
              workspaceId,
            }}
          />
        ) : (
          <DocumentSidebar
            availableTabs={[
              DocumentTab.TABLE_OF_CONTENTS,
              DocumentTab.DOCUMENT_METADATA,
              DocumentTab.DOCUMENT_REFERENCES,
              DocumentTab.VERSION_HISTORY,
            ]}
            documentId={sidebarDocumentId}
            selectionResetKey={blobName}
            blobId={blobIdToFetch || undefined}
            currentRevisionVersion={revisionVersionToFetch}
            isLoading={isPageLoading}
            workspaceDocument={routeDocument ?? undefined}
            createReferenceTarget={{
              documentId: sidebarDocumentId,
              source: DataSource.Workspace,
              workspaceId,
            }}
          />
        )
      }
    >
      <PrintStyles />
      {isPageLoading ? (
        <div className="h-[calc(100vh-3rem)] flex items-center justify-center">
          <div className="flex flex-col items-center gap-2 mt-12">
            <Spinner className="size-12" />
            <p className="text-sm text-muted-foreground">Henter dokument...</p>
          </div>
        </div>
      ) : html ? (
        <div
          className="flex flex-col print-bg workspace-doc-root"
          style={{ height: "calc(100vh - var(--appbar-height))" }}
        >
          {/* Sticky header with zoom and print */}
          <div className="sticky top-0 z-10 bg-background border-b no-print">
            <DocumentViewerToolbar>
              <WorkspaceDocumentHeader
                onPrint={() => void handlePrintDocumentOnly(workspaceDocument?.name)}
                searchProps={searchProps}
                zoomLevel={zoomLevel}
                onZoomChange={setZoomLevel}
                onFitToWidth={handleFitToWidth}
                favoriteSubscribe={favoriteSubscribe}
              />
            </DocumentViewerToolbar>
          </div>

          <WorkspaceDocumentStyles />

          {/* Document content area */}
          <div
            ref={containerRef}
            className="workspace-doc-container flex-1 overflow-auto"
          >
            <DocumentRenderer
              // Keyed by document identity, not payload: the served HTML embeds
              // short-lived SAS tokens, so its string identity can change on a
              // refetch without the document having changed. Keying on the
              // string remounted the renderer (and reset scroll) every time.
              key={`${workspaceId}:${blobIdToFetch}`}
              rawHTML={rawHTML}
              rawHtmlIsSanitized
              rawCSS={rawCSS}
              highlightIds={highlightIds}
              scrollToId={isRendering ? undefined : scrollToId}
              highlightMatchText={refMatchText}
              highlightChunkText={refChunkText}
              currentSource={"Workspace"}
              currentDocId={sidebarDocumentId}
              currentWorkspaceId={workspaceId}
              scrollMemoryKey={
                workspaceId && sidebarDocumentId
                  ? `workspace:${workspaceId}:${sidebarDocumentId}`
                  : undefined
              }
              shouldGenerateDarkModeCss={false}
              isCurrent={!isWorkspaceDocumentHistoric(workspaceDocument)}
              watermarkFixedColor={true}
              onContentCommitted={searchProps.onContentCommitted}
            />

            <div className="no-print">
              <ScrollToTopFab />
            </div>
          </div>
        </div>
      ) : (
        <div className="h-[calc(100vh-3rem)] flex items-center justify-center px-4">
          <p className="text-sm text-muted-foreground">
            Dokumentet kunne ikke vises.
          </p>
        </div>
      )}
    </PageContent>
  );
};

export default WorkspaceDocumentPage;
